import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Button, Card, Typography, message,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { REGION_LABELS } from '../constants';
import { buildRepUpdateText, formatCount, formatSignedPct } from '../runUtils';

const { Paragraph, Text, Title } = Typography;

const MOVER_GROUPS = [
  { kind: 'newChampion', label: 'New Champions' },
  { kind: 'outOfAtRisk', label: 'Out of At Risk' },
  { kind: 'intoAtRisk', label: 'Into At Risk' },
  { kind: 'developing', label: 'Developing' },
];

const HIDDEN_CHANNELS = new Set(['Other']);

const pluralCustomers = (n) => `${formatCount(n)} ${n === 1 ? 'customer' : 'customers'}`;

const FormattedUpdate = ({ run }) => {
  const digest = run?.summaryJson?.repDigest;
  const regionLabel = REGION_LABELS[run?.region] ?? run?.region;
  const refDate = run?.summaryJson?.scoring?.referenceDate || run?.createdAt;
  const month = refDate && dayjs(refDate).isValid() ? dayjs(refDate).format('MMMM YYYY') : null;
  const heading = ['Trade customer update', regionLabel, month].filter(Boolean).join(' — ');

  if (!digest || typeof digest !== 'object') return null;

  const team = digest.team || {};
  const channels = (Array.isArray(digest.channels) ? digest.channels : [])
    .filter((channel) => channel && !HIDDEN_CHANNELS.has(channel.channel) && formatSignedPct(channel.fyPct));
  const reps = Array.isArray(digest.reps) ? digest.reps : [];
  const teamPct = formatSignedPct(team.fyPct);

  const wins = [];
  if (team.outOfAtRisk) wins.push(`${pluralCustomers(team.outOfAtRisk)} moved out of At Risk`);
  if (team.newChampions) {
    wins.push(`${formatCount(team.newChampions)} new ${team.newChampions === 1 ? 'Champion' : 'Champions'}`);
  }

  const watch = [];
  if (team.intoAtRisk) watch.push(`${pluralCustomers(team.intoAtRisk)} slipped into At Risk`);
  if (team.championsToAtRisk) {
    const label = team.championsToAtRisk === 1 ? 'Champion' : 'Champions';
    watch.push(`${formatCount(team.championsToAtRisk)} ${label} now At Risk`);
  }

  let overall = null;
  if (teamPct) {
    overall = `FY forecast tracking ${teamPct} vs last FY across ${pluralCustomers(team.customers ?? 0)}`;
  } else if (team.customers) {
    overall = `${pluralCustomers(team.customers)} scored`;
  }

  return (
    <Card className="segmentation-section" title="Sales team update">
      <div className="segmentation-rep-digest">
        <Title level={5} style={{ marginTop: 0 }}>{heading}</Title>
        {overall && <Paragraph style={{ marginBottom: 12 }}>{overall}</Paragraph>}
        {channels.length > 0 && (
          <div className="segmentation-rep-row">
            <Text type="secondary">Channels</Text>
            <div>
              {channels.map((channel) => (
                <Text key={channel.channel} className="segmentation-rep-channel">
                  {`${channel.channel} ${formatSignedPct(channel.fyPct)}`}
                </Text>
              ))}
            </div>
          </div>
        )}
        {wins.length > 0 && (
          <div className="segmentation-rep-row">
            <Text type="secondary">Wins</Text>
            <ul>
              {wins.map((line) => <li key={line}>{line}</li>)}
            </ul>
          </div>
        )}
        {watch.length > 0 && (
          <div className="segmentation-rep-row">
            <Text type="secondary">Watch</Text>
            <ul>
              {watch.map((line) => <li key={line}>{line}</li>)}
            </ul>
          </div>
        )}
        {reps.map((rep) => {
          const movers = Array.isArray(rep.movers) ? rep.movers : [];
          const meta = [`${pluralCustomers(rep.customers ?? 0)}`];
          const repPct = formatSignedPct(rep.fyPct);
          if (repPct) meta.push(`FY ${repPct}`);
          if (rep.up || rep.down) meta.push(`${rep.up ?? 0} up / ${rep.down ?? 0} down`);
          const groups = MOVER_GROUPS.map(({ kind, label }) => {
            const names = movers
              .filter((mover) => mover && mover.kind === kind)
              .map((mover) => mover.customerName || mover.customerId)
              .filter(Boolean);
            return names.length ? { kind, label, names } : null;
          }).filter(Boolean);
          return (
            <div key={rep.rep} className="segmentation-rep-block">
              <Text strong>{rep.rep}</Text>
              <Text type="secondary">{meta.join(' · ')}</Text>
              {groups.length > 0 && (
                <ul>
                  {groups.map((group) => (
                    <li key={group.kind}>
                      <Text strong>{group.label}</Text>
                      {`: ${group.names.join(', ')}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

FormattedUpdate.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  run: PropTypes.object,
};

FormattedUpdate.defaultProps = {
  run: null,
};

const canUseClipboard = () => (
  typeof navigator !== 'undefined'
  && !!navigator.clipboard
  && typeof navigator.clipboard.writeText === 'function'
);

const RepUpdateCopy = ({ run, formatted }) => {
  const text = useMemo(() => buildRepUpdateText(
    run?.summaryJson,
    REGION_LABELS[run?.region] ?? run?.region,
    run?.createdAt,
  ), [run]);

  if (formatted) return <FormattedUpdate run={run} />;

  if (!text) return null;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('Sales team update copied');
    } catch (err) {
      message.error('Could not copy — select the text and copy manually');
    }
  };

  const clipboardAvailable = canUseClipboard();

  return (
    <Card
      className="segmentation-section"
      title="Sales team update"
      extra={clipboardAvailable ? (
        <Button icon={<CopyOutlined />} onClick={onCopy}>Copy</Button>
      ) : null}
    >
      <Paragraph type="secondary">
        Plain-text summary for the sales rep channel: where customers stand and how the FY is
        tracking, then a short block per rep. Paste it straight into Slack or email.
      </Paragraph>
      {clipboardAvailable ? (
        <pre className="segmentation-rep-update">{text}</pre>
      ) : (
        <Paragraph copyable={{ text }}>
          <pre className="segmentation-rep-update">{text}</pre>
        </Paragraph>
      )}
    </Card>
  );
};

RepUpdateCopy.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  run: PropTypes.object,
  formatted: PropTypes.bool,
};

RepUpdateCopy.defaultProps = {
  run: null,
  formatted: false,
};

export default RepUpdateCopy;
